/**
 * The pure half of `ClearanceEnvelope` (`components/3D/ClearanceEnvelopePreview.tsx`):
 * which of the two possible sources — the dialog's live preview atom, or a
 * finished run's immutable snapshot — the envelope is drawn from. Split out so
 * the collapse logic is exhaustively unit-testable without an r3f harness
 * (ruling P11), the same reasoning `clearanceBandSource` in `bands.ts` was
 * split out for.
 */
import type { ClearanceJson, ClearanceVertex } from './types';

/** What `clearancePreviewAtom` carries — duplicated here (not imported from
 *  the atom module) so this file stays DOM/atom-free and testable in
 *  isolation; the shape is kept in lock-step with the atom by the component
 *  that bridges the two. */
export interface ClearanceEnvelopePreviewValue {
  key: string;
  vertices: ClearanceVertex[];
  verticalOffsetM: number;
  from: number | null;
  to: number | null;
}

/**
 * What the envelope is drawn from. The dialog's live form, or a finished
 * run's immutable snapshot — never both, and never the live PROFILE EDITOR
 * state for a finished run (scenario C5: two runs must not render the same
 * envelope).
 */
export interface ClearanceEnvelopeSource extends ClearanceEnvelopePreviewValue {
  /** Amber while a run's alignment has drifted (principle P5); null while the
   *  dialog preview is the source (it has nothing to drift from) or while the
   *  run source's centreline matches. */
  qualified: string | null;
}

/**
 * The dialog wins while it is open: the user is actively editing a profile,
 * and an old run's envelope drawn over the one they are dragging would be two
 * tubes claiming to be "the" profile. Only once the dialog atom is empty does
 * a finished run's own snapshot — never the live profile editor state, which
 * may since have been edited into an unrelated shape — take over, and only
 * while the reader has left the dock open on that run's dataset with the
 * toggle on.
 */
export function clearanceEnvelopeSource(
  preview: ClearanceEnvelopePreviewValue | null,
  hasDock: boolean,
  envelopeOn: boolean,
  json: Pick<ClearanceJson, 'alignment' | 'profile' | 'params'> | null,
  qualified: string | null,
): ClearanceEnvelopeSource | null {
  if (preview) return { ...preview, qualified: null };
  if (!hasDock || !envelopeOn || !json) return null;
  return {
    key: json.alignment.key,
    vertices: json.profile.vertices,
    verticalOffsetM: json.params.verticalOffsetM,
    from: json.params.stationFromInternalM,
    to: json.params.stationToInternalM,
    qualified,
  };
}
